import React, { useState, Fragment, useMemo } from "react";
import { formatCurrency, formatNumber, formatUnits } from "./api.js";

// ✅ Chart color palette
const CHART_COLORS = Array.from({ length: 1750 }, (_, i) => {
  const hue = (i * 137.508) % 360;
  const saturation = 65 + (i % 3) * 10;
  const lightness = 45 + (i % 4) * 8;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
});

/**
 * Optimized Donut Chart
 */
function DonutChartWithTooltip({ data, centerLabel, centerValue }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const FALLBACK_PALETTE = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const size = 220;
  const outerRadius = 95;
  const innerRadius = 60;
  const cx = size / 2;
  const cy = size / 2;

  let cumulativeAngle = -90;

  const slices = data.map((item, index) => {
    const rawAngle = (item.value / total) * 360;
    const angle = rawAngle >= 360 ? 359.99 : rawAngle;
    const startAngle = cumulativeAngle;
    const endAngle = cumulativeAngle + angle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = cx + outerRadius * Math.cos(startRad);
    const y1 = cy + outerRadius * Math.sin(startRad);
    const x2 = cx + outerRadius * Math.cos(endRad);
    const y2 = cy + outerRadius * Math.sin(endRad);
    const x3 = cx + innerRadius * Math.cos(endRad);
    const y3 = cy + innerRadius * Math.sin(endRad);
    const x4 = cx + innerRadius * Math.cos(startRad);
    const y4 = cy + innerRadius * Math.sin(startRad);

    const largeArc = angle > 180 ? 1 : 0;
    const path = `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`;

    const midAngle = (startAngle + endAngle) / 2;
    const midRad = (midAngle * Math.PI) / 180;
    const labelX = cx + ((outerRadius + innerRadius) / 2) * Math.cos(midRad);
    const labelY = cy + ((outerRadius + innerRadius) / 2) * Math.sin(midRad);

    cumulativeAngle = endAngle;

    return { 
      ...item, 
      path, 
      percentage: (item.value / total) * 100, 
      labelX, 
      labelY, 
      index,
      color: item.color || FALLBACK_PALETTE[index % FALLBACK_PALETTE.length]
    };
  });

  return (
    <div className="relative flex flex-col items-center w-full">
      <div className="relative">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          onMouseLeave={() => setHoveredIndex(null)}
          className="drop-shadow-sm"
        >
          {slices.map((slice) => {
            const strokeWidth = data.length > 30 ? "0.2" : data.length > 10 ? "0.8" : "2";
            return (
              <g key={slice.index}>
                <path
                  d={slice.path}
                  fill={slice.color}
                  stroke="white"
                  strokeWidth={strokeWidth}
                  className="cursor-pointer transition-all duration-300"
                  style={{
                    opacity: hoveredIndex === null || hoveredIndex === slice.index ? 1 : 0.4,
                    transform: hoveredIndex === slice.index ? 'scale(1.03)' : 'scale(1)',
                    transformOrigin: 'center',
                  }}
                  onMouseEnter={(e) => {
                    setHoveredIndex(slice.index);
                    setMousePos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                />
                {/* Only show percentage on large enough slices */}
                {slice.percentage > 5 && (
                  <text
                    x={slice.labelX}
                    y={slice.labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize="10"
                    fontWeight="800"
                    pointerEvents="none"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                  >
                    {slice.percentage.toFixed(1)}%
                  </text>
                )}                
              </g>
            );
          })}
          <text x={cx} y={cy - 8} textAnchor="middle" fontSize="10" fill="#94a3b8" fontWeight="700" className="uppercase tracking-widest">{centerLabel}</text>
          <text x={cx} y={cy + 12} textAnchor="middle" fontSize="16" fill="#1e293b" fontWeight="800">{centerValue}</text>
        </svg>
      </div>

      {hoveredIndex !== null && (
        <div
          className="fixed z-[9999] pointer-events-none bg-white/95 backdrop-blur shadow-2xl rounded-xl px-4 py-3 border border-slate-200 ring-1 ring-black/5"
          style={{ left: mousePos.x + 15, top: mousePos.y + 15, minWidth: '200px' }}
        >
          <div className="flex items-center gap-2 mb-2 border-b border-slate-100 pb-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: slices[hoveredIndex].color }} />
            <div className="text-xs font-black text-slate-600 uppercase tracking-tight truncate">{slices[hoveredIndex].label}</div>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-slate-500 font-bold uppercase">Revenue</span>
              <span className="text-sm font-bold text-slate-900">{formatCurrency(slices[hoveredIndex].revenue)}</span>
            </div>
            <div className="flex justify-between items-center pt-1 mt-1 border-t border-slate-50">
              <span className="text-[10px] text-slate-500 font-bold uppercase">Share</span>
              <span className="text-sm font-black text-indigo-600">{slices[hoveredIndex].percentage.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * MAIN COMPONENT
 */
export default function SalesByCategorySection({ query }) {
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  if (query.loading) return <Skeleton />;

  const families = query.data?.families || [];
  const totals = query.data?.totals || {};

  // Filter families based on search term
  const filteredFamilies = families.filter((f) =>
    f.family.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (families.length === 0) {
    return <EmptyState label="No category sales found for this filter set." />;
  }

  const toggleFamily = (familyName) => {
    setExpandedFamilies((prev) => ({
      ...prev,
      [familyName]: !prev[familyName],
    }));
  };

  return (
    <div className="w-full space-y-4">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
        
        {/* Left Column: Formats Donut */}
        <div className="flex flex-col items-center justify-start rounded-xl bg-slate-50 p-6 border border-slate-100 h-fit lg:sticky lg:top-4">
          <DonutChartWithTooltip
            centerLabel="By Format Family"
            centerValue={families.length}
            data={families.map((f, i) => ({
              label: f.family,
              value: f.revenue,
              revenue: f.revenue,
              color: CHART_COLORS[i % CHART_COLORS.length]
            }))}
          />
        </div>

        {/* Right Column: Families Table */}
        <div className="space-y-4">
          
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-4 w-4 text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="Search category families..."
              className="block w-full pl-10 pr-3 py-2 border border-slate-200 rounded-xl leading-5 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Change overflow-hidden to overflow-x-auto to allow horizontal scrolling */}
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            {/* Added min-w-full to ensure it fills the container */}
            <table className="w-full text-sm min-w-full">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                <tr>
                  <Th className="w-10"></Th>
                  {/* Added whitespace-nowrap to headers to prevent text breaking */}
                  <Th className="whitespace-nowrap">Category Family</Th>
                  <Th align="center" className="whitespace-nowrap">Categories</Th>
                  <Th align="right" className="whitespace-nowrap">Revenue</Th>                
                  <Th align="right" className="whitespace-nowrap">Units</Th>
                  <Th align="right" className="pr-6 whitespace-nowrap">Share</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredFamilies.length > 0 ? (
                  filteredFamilies.map((family) => {
                    const isExpanded = !!expandedFamilies[family.family];
                    return (
                      <Fragment key={family.family}>
                        <tr 
                          className="hover:bg-slate-50/50 cursor-pointer transition-colors" 
                          onClick={() => toggleFamily(family.family)}
                        >
                          <td className="pl-4 py-4 text-slate-400">
                            {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                          </td>
                          {/* Added whitespace-nowrap to cells */}
                          <td className="py-4 font-bold text-slate-900 whitespace-nowrap">{family.family}</td>
                          <td className="py-4 text-center whitespace-nowrap">
                            <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                              {family.categoryCount}
                            </span>
                          </td>
                          <td className="py-4 text-right font-bold whitespace-nowrap">{formatCurrency(family.revenue)}</td>                      
                          <td className="py-4 text-right whitespace-nowrap">{formatUnits(family.unitsSold)}</td>                      
                          <td className="py-4 text-right pr-6 whitespace-nowrap">
                             <div className="flex flex-col items-end">
                                <span className="text-xs font-bold text-slate-900">{family.share}%</span>
                                <div className="w-12 h-1 bg-slate-100 rounded-full mt-1 overflow-hidden">
                                    <div className="h-full bg-slate-400" style={{ width: `${family.share}%` }} />
                                </div>
                             </div>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="bg-slate-50/30">
                            <td colSpan={6} className="px-8 py-0">
                              <div className="max-h-[240px] overflow-y-auto my-4 border border-slate-200 rounded-lg bg-white shadow-inner custom-scrollbar">
                                <table className="w-full">
                                  <thead className="sticky top-0 bg-slate-100 z-10 shadow-sm">
                                    <tr className="text-[10px] uppercase text-slate-500">
                                      <th className="text-left px-4 py-2 font-bold whitespace-nowrap">Category</th>
                                      <th className="text-right px-4 py-2 font-bold whitespace-nowrap">Revenue</th>
                                      <th className="text-right px-4 py-2 font-bold whitespace-nowrap">Units</th>                                
                                      <th className="text-right px-4 py-2 font-bold pr-6 whitespace-nowrap">% of Family</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {family.categories.map((cat) => (
                                      <tr key={cat.category} className="text-[12px] hover:bg-blue-50/30 transition-colors">
                                        <td className="px-4 py-2.5 text-blue-600 font-medium whitespace-nowrap">{cat.category}</td>
                                        <td className="px-4 py-2.5 text-right font-semibold text-slate-700 whitespace-nowrap">{formatCurrency(cat.revenue)}</td>
                                        <td className="px-4 py-2.5 text-right text-slate-500 whitespace-nowrap">{formatUnits(cat.unitsSold)}</td>                                  
                                        <td className="px-4 py-2.5 text-right pr-6 font-black text-slate-900 whitespace-nowrap">{cat.share}%</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-400 italic">
                      No families match "{searchTerm}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
        </div>
      </div>
      
      {/* Optional CSS for the scroller */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}

// Internal Icons for the Search and Chevrons
const SearchIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg> 
);

// Icons
const ChevronRightIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>;
const ChevronDownIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>;

// Helpers
function Th({ children, align = "left", className = "" }) {
  return (
    <th className={`px-4 py-3 text-${align} text-[10px] font-bold uppercase tracking-widest text-slate-400 ${className}`}>
      {children}
    </th>
  );
}

function Skeleton() {
  return (
    <div className="w-full space-y-4 animate-pulse">
      <div className="h-64 bg-slate-100 rounded-xl" />
      <div className="h-96 bg-slate-50 rounded-xl" />
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}